/**
 * Brands Filter Component
 *
 * Searchable brand list with alphabetical grouping.
 * Figma: Filter Section - Brands (Node: 358:18120)
 *
 * Features: Search input, alphabetical sections, checkboxes, scrollable
 */
"use client";

import { brandSearchErrorMessage } from "@/lib/brand-search-state";

import React, { useMemo, useRef, useState } from "react";

import { searchBrandNamesByPrefix } from "@/lib/shop-agent-client";
import { colors } from "@/styles/design-tokens";

import { Checkbox } from "./Checkbox";
import { FilterCategoryHeading } from "./FilterCategoryHeading";

const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;

export interface BrandOption {
  id: string;
  label: string;
}

interface BrandsFilterProps {
  brands: BrandOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isLoading?: boolean;
  className?: string;
}

export function BrandsFilter({
  brands,
  selected,
  onChange,
  isLoading = false,
  className = "",
}: BrandsFilterProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<BrandOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchRequestIdRef = useRef(0);

  // Case-insensitive check if brand is selected
  const isBrandSelected = (brandId: string) =>
    selected.some((id) => id.toLowerCase() === brandId.toLowerCase());

  const toggleBrand = (brandId: string) => {
    // Use case-insensitive comparison for toggle
    const isCurrentlySelected = isBrandSelected(brandId);
    const newSelected = isCurrentlySelected
      ? selected.filter((id) => id.toLowerCase() !== brandId.toLowerCase())
      : [...selected, brandId];
    onChange(newSelected);

    if (searchQuery.trim().length > 0) {
      searchRequestIdRef.current += 1;
      setSearchQuery("");
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
    }
  };

  const trimmedQuery = searchQuery.trim();
  const isSearchActive = trimmedQuery.length > 0;

  React.useEffect(() => {
    let cancelled = false;

    if (!isSearchActive) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      return () => {
        cancelled = true;
      };
    }

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(`Type at least ${MIN_QUERY_LENGTH} characters to search`);
      return () => {
        cancelled = true;
      };
    }

    const handle = window.setTimeout(() => {
      const requestId = searchRequestIdRef.current + 1;
      searchRequestIdRef.current = requestId;
      setIsSearching(true);
      setSearchError(null);

      (async () => {
        try {
          const names = await searchBrandNamesByPrefix(trimmedQuery, 25);
          if (cancelled || searchRequestIdRef.current !== requestId) return;

          const seen = new Set<string>();
          const options: BrandOption[] = [];
          for (const rawName of Array.isArray(names) ? names : []) {
            const name = String(rawName || "").trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            options.push({ id: name, label: name });
          }
          setSearchResults(options);
        } catch (error) {
          if (!cancelled && searchRequestIdRef.current === requestId) {
            console.error("BrandsFilter prefix search failed:", error);
            setSearchResults([]);
            setSearchError(brandSearchErrorMessage(error));
          }
        } finally {
          if (!cancelled && searchRequestIdRef.current === requestId) {
            setIsSearching(false);
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [isSearchActive, trimmedQuery]);

  const activeBrands = useMemo(() => {
    if (isSearchActive) {
      return searchResults;
    }
    return brands;
  }, [brands, isSearchActive, searchResults]);

  const groupedBrands = useMemo(() => {
    return activeBrands.reduce(
      (acc, brand) => {
        if (!brand?.label) return acc;
        const firstLetter = brand.label[0]?.toUpperCase() || "#";
        if (!acc[firstLetter]) {
          acc[firstLetter] = [];
        }
        acc[firstLetter].push(brand);
        return acc;
      },
      {} as Record<string, BrandOption[]>
    );
  }, [activeBrands]);

  const sortedLetters = useMemo(() => Object.keys(groupedBrands).sort(), [groupedBrands]);
  const noBrandsFound = sortedLetters.length === 0;
  const showNoBrandsState = !isSearching && !searchError && isSearchActive && noBrandsFound;

  return (
    <div className={className}>
      {/* Heading */}
      <FilterCategoryHeading
        label={`BRANDS (${selected.length})`}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      {/* Search Input */}
      {isExpanded && (
        <div
          style={{
            marginTop: "9.983px",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "35.998px",
              position: "relative",
              backgroundColor: colors.background.white,
              border: `1px solid ${colors.divider.light}`,
              borderRadius: "4px",
            }}
          >
            {/* Search Icon */}
            <svg
              width="15.996"
              height="15.996"
              viewBox="0 0 16 16"
              fill="none"
              style={{
                position: "absolute",
                left: "9.996px",
                top: "10.001px",
              }}
            >
              <circle cx="7" cy="7" r="5" stroke={colors.text.accentMdGray} strokeWidth="1.5" />
              <path
                d="M11 11L14 14"
                stroke={colors.text.accentMdGray}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>

            {/* Input */}
            <input
              type="text"
              placeholder="Search brands"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="text-action-muted text-ink"
              style={{
                width: "100%",
                height: "100%",
                paddingLeft: "36px",
                paddingRight: "12px",
                border: "none",
                outline: "none",
                backgroundColor: "transparent",
              }}
            />
          </div>

          {/* Brand List */}
          <div
            style={{
              marginTop: "13.974px",
              maxHeight: "300px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLoading && !isSearchActive ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">
                Loading brands...
              </div>
            ) : isSearching ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">Searching…</div>
            ) : searchError ? (
              <div className="text-action-muted text-ink-secondary text-center p-5">
                {searchError}
              </div>
            ) : (
              <>
                {sortedLetters.map((letter) => (
                  <div key={letter}>
                    {/* Letter Header */}
                    <div
                      className="text-action text-ink"
                      style={{
                        marginTop: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      {letter}
                    </div>

                    {/* Brands under this letter */}
                    {groupedBrands[letter].map((brand, index) => (
                      <div
                        key={brand.id}
                        onClick={() => toggleBrand(brand.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          height: "23.988px",
                          marginTop: index > 0 ? "10px" : "0px", // 10px gap between items
                          cursor: "pointer",
                        }}
                      >
                        {/* Checkbox - 12px × 12px */}
                        <div
                          style={{
                            paddingTop: "3.996px",
                          }}
                        >
                          <Checkbox
                            checked={isBrandSelected(brand.id)}
                            onChange={() => toggleBrand(brand.id)}
                          />
                        </div>

                        {/* Label */}
                        <span
                          className="text-action text-ink"
                          style={{
                            marginLeft: "23.988px", // Gap between checkbox and text (35.988 - 12)
                            paddingTop: "4.654px",
                          }}
                        >
                          {brand.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}

                {showNoBrandsState && (
                  <div className="text-action-muted text-ink-secondary text-center p-5">
                    No brands found
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          width: "100%",
          height: "0.882px",
          backgroundColor: colors.divider.light,
          marginTop: isExpanded ? "15px" : "0px",
        }}
      />
    </div>
  );
}
